import { queryClient } from "App";
import { get } from "lodash";
import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { formatError } from "helpers/formatError";
import {
  getCustomFieldGroupsTableColumns,
  getCustomFieldGroupsTableFilters,
} from "routes/custom_field_group/components/CustomFieldGroupsTableColumnsAndFilters";
import { CUSTOM_FIELD_GROUPS_TABLE_CONSTANTS } from "routes/custom_field_group/CustomFieldGroupsTableV3";
import CustomFieldGroupsService from "services/CustomFieldGroups";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";

export const invalidateCustomFieldGroupsTableQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [CUSTOM_FIELD_GROUPS_TABLE_CONSTANTS.queryKey],
  });
};

export const useCustomFieldGroupsMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data => CustomFieldGroupsService.createCustomFieldGroup(data),
    onSuccess: () => invalidateCustomFieldGroupsTableQuery(),
  });

  const update = useMutation({
    mutationFn: data => CustomFieldGroupsService.editCustomFieldGroup(data),
    onSuccess: () => invalidateCustomFieldGroupsTableQuery(),
  });

  const remove = useMutation({
    mutationFn: id => CustomFieldGroupsService.deleteCustomFieldGroup(id),
    onSuccess: () => {
      invalidateCustomFieldGroupsTableQuery();
      toast.success("Custom field group deleted");
      onDeleted?.();
    },
    onError: err =>
      toast.error(formatError(err, "Failed to delete custom field group")),
  });

  return { create, update, remove };
};

export const useCustomFieldGroupsTable = ({ tableName = null }) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    return getCustomFieldGroupsTableFilters();
  }, []);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getCustomFieldGroupsTableColumns();

    const customFieldGroupsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: customFieldGroupsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [tableName, tableSettings, isTableSettingsFetched]);

  return { filters, columns };
};
